import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, CircularProgress, Card, CardContent } from "@mui/material";
import apiClient from "../../utils/apiClient";

const CropRotation = () => {
  const [rotationPlans, setRotationPlans] = useState({
    three_year: null,
    four_year: null,
    six_year: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRotationPlans();
  }, []);

  const fetchRotationPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const [threeYearRes, fourYearRes, sixYearRes] = await Promise.all([
        apiClient.get("/rotation-family-rules/three_year"),
        apiClient.get("/rotation-family-rules/four_year"),
        apiClient.get("/rotation-family-rules/six_year"),
      ]);

      setRotationPlans({
        three_year: threeYearRes.data,
        four_year: fourYearRes.data,
        six_year: sixYearRes.data,
      });
    } catch (err) {
      console.error("Error fetching rotation plans:", err);
      setError("Failed to load rotation plans");
    } finally {
      setLoading(false);
    }
  };

  const renderRotationPlan = (plan, title) => {
    if (!plan || !plan.groups || plan.groups.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
          No rotation plan configured yet. Configure it in the Management page.
        </Typography>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        {plan.groups.map((group) => (
          <Card
            key={group.group_number}
            sx={{
              mb: 2,
              background: 'var(--color-aged-paper)',
              border: '2px solid var(--color-border-vintage)',
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'var(--font-decorative)',
                  color: 'var(--color-sepia)',
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Group {group.group_number}: {group.group_name}
              </Typography>
              {group.crops && group.crops.length > 0 ? (
                <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
                  {group.crops.map((crop) => (
                    <Typography
                      key={crop.crop_uuid}
                      component="li"
                      variant="body2"
                      sx={{
                        fontFamily: 'var(--font-serif)',
                        color: 'var(--color-ink)',
                        mb: 0.5,
                      }}
                    >
                      {crop.name}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  No crops assigned to this group.
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h3" gutterBottom className="vintage-script-header">
          Crop Rotation
        </Typography>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h3" gutterBottom className="vintage-script-header">
        Crop Rotation
      </Typography>
      
      <Paper sx={{
        p: 3,
        background: 'transparent',
        border: '2px solid var(--color-border-vintage)',
        borderRadius: 0,
        boxShadow: 'none',
        position: 'relative',
        mb: 4
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, var(--color-accent-gold) 0%, transparent 50%, var(--color-accent-gold) 100%)'
        }}></div>
        
        <Typography variant="body1" sx={{
          fontFamily: 'var(--font-serif)',
          color: 'var(--color-ink)',
          lineHeight: 1.6,
          mt: 2
        }}>
          Discover the principles of crop rotation to maintain soil health and 
          prevent disease. Build a sustainable gardening practice that benefits 
          both your plants and the environment.
        </Typography>
        
        <Typography variant="body1" sx={{
          fontFamily: 'var(--font-serif)',
          color: 'var(--color-ink)',
          lineHeight: 1.6,
          mt: 2
        }}>
          Learn how to use Agritur's crop rotation features to plan your garden 
          layout, track soil effects, and maintain healthy growing conditions 
          year after year.
        </Typography>
      </Paper>

      {/* 3 Year Rotation Plan */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          className="rotation-plan-subheader"
          sx={{
            fontFamily: 'var(--font-decorative)',
            color: 'var(--color-sepia)',
            fontWeight: 600,
            mb: 2,
            borderBottom: '2px solid var(--color-border-vintage)',
            pb: 1,
          }}
        >
          3 Year Rotation Plan
        </Typography>
        {renderRotationPlan(rotationPlans.three_year, "3 Year")}
      </Box>

      {/* 4 Year Rotation Plan */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          className="rotation-plan-subheader"
          sx={{
            fontFamily: 'var(--font-decorative)',
            color: 'var(--color-sepia)',
            fontWeight: 600,
            mb: 2,
            borderBottom: '2px solid var(--color-border-vintage)',
            pb: 1,
          }}
        >
          4 Year Rotation Plan
        </Typography>
        {renderRotationPlan(rotationPlans.four_year, "4 Year")}
      </Box>

      {/* 6 Year Rotation Plan */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          className="rotation-plan-subheader"
          sx={{
            fontFamily: 'var(--font-decorative)',
            color: 'var(--color-sepia)',
            fontWeight: 600,
            mb: 2,
            borderBottom: '2px solid var(--color-border-vintage)',
            pb: 1,
          }}
        >
          6 Year Rotation Plan
        </Typography>
        {renderRotationPlan(rotationPlans.six_year, "6 Year")}
      </Box>
    </Box>
  );
};

export default CropRotation;
